import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useModal from 'Hooks/useModal';
import isEmpty from 'lodash/isEmpty';
import intl from 'react-intl-universal';
import { MEDIUM_INPUTS_LENGTH } from 'utils/constants';
import { FormItem, TextField } from '@getsynapse/design-system';
import { NewDashboard, objKeyAsString } from 'utils/customTypes';
import { createDashboard } from 'state/normalized/dashboard/dashboardSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { showNotificationBanner } from 'state/InlineNotification/inlineNotificationSlice';
import { useAppThunkDispatch } from 'state/store';

const CreateDashboardModal: React.FC<{
  isOpen: boolean;
  onCloseModal: () => void;
  afterCreateDashboard?: (newId: string) => void;
}> = ({ isOpen = false, onCloseModal, afterCreateDashboard }) => {
  const { Modal, modalProps, openModal, closeModal } = useModal();
  const [data, setData] = useState<NewDashboard>({
    name: '',
  });
  const dispatch = useAppThunkDispatch();

  useEffect(() => {
    if (isOpen && !modalProps.isOpen) {
      openModal(modalProps);
    }
  }, [openModal, modalProps, isOpen]);

  const handleCloseModal = useCallback(() => {
    onCloseModal();
    closeModal();
  }, [onCloseModal, closeModal]);

  const canSave = useMemo(() => {
    const requiredFields = Object.keys(data) as Array<keyof NewDashboard>;
    const missingRequiredFields = requiredFields.some((field) =>
      isEmpty(data[field])
    );

    return !missingRequiredFields;
  }, [data]);

  const handleSave = useCallback(async () => {
    try {
      const createdDashboard = await dispatch(
        createDashboard({
          prototype: {
            name: data.name,
          },
        })
      ).unwrap();

      dispatch(
        showNotificationBanner({
          notificationVariant: 'success',
          notificationText: intl.get('CREATE_DASHBOARD_MODAL.SUCCESS_MESSAGE', {
            name: data.name,
          }),
        })
      );

      const createDashboardId = createdDashboard.dashboard.id;
      if (afterCreateDashboard) {
        afterCreateDashboard(createDashboardId);
      } else {
        handleCloseModal();
      }
    } catch (error) {
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get('CREATE_DASHBOARD_MODAL.ERROR_TITLE'),
          notificationMessage: intl.get('CREATE_DASHBOARD_MODAL.ERROR_MESSAGE'),
        })
      );
      handleCloseModal();
    }
  }, [data, dispatch, handleCloseModal, afterCreateDashboard]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('CREATE_DASHBOARD_MODAL.CREATE'),
        variant: 'primary',
        className: 'h-10',
        disabled: !canSave,
        loading: false,
        onClick: () => handleSave(),
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModal,
        className: 'h-10',
      },
    ],
    [canSave, handleCloseModal, handleSave]
  );

  const updateValue = (newValue: objKeyAsString) => {
    setData((prevState: NewDashboard) => ({
      ...prevState,
      ...newValue,
    }));
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const isEnterKey = event.key === 'Enter';

      if (!isEnterKey || !data.name) return;
      handleSave();
    },
    [data, handleSave]
  );

  return (
    <Modal
      {...modalProps}
      aria-label={intl.get('CREATE_DASHBOARD_MODAL.TITLE')}
      title={intl.get('CREATE_DASHBOARD_MODAL.TITLE')}
      titleIcon={{
        name: 'add-circle-outline',
        className: 'text-primary-dark',
      }}
      size='medium'
      actionButtons={actionButtons}
      closeModal={handleCloseModal}
    >
      <div className='grid grid-cols-1 gap-y-4'>
        <FormItem>
          <TextField
            label={intl.get('CREATE_DASHBOARD_MODAL.DASHBOARD_NAME')}
            labelProps={{
              required: true,
            }}
            displayCharactersCount
            maxLength={MEDIUM_INPUTS_LENGTH}
            value={data.name}
            onKeyPress={handleKeyDown}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateValue({ name: e.target.value })
            }
            placeholder={intl.get(
              'CREATE_DASHBOARD_MODAL.DASHBOARD_NAME_PLACEHOLDER'
            )}
            className='w-full'
            data-cy='dashboard-input_name'
          />
        </FormItem>
      </div>
    </Modal>
  );
};

export default CreateDashboardModal;
